import React, { useState, useRef, useEffect, useCallback } from "react";

interface Props {
  dollars: string;
  setDollars: (dollars: string) => void;
  mainCoin: string;
  setMainCoin: (mainCoin: string) => void;
  handleCoinChange: (value: string, dollarValue: number) => string;
}

const Form: React.FC<Props> = ({
  dollars,
  setDollars,
  mainCoin,
  setMainCoin,
  handleCoinChange,
}) => {
  const [usdValue, setUsdValue] = useState("");
  const [clpValue, setClpValue] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [shake, setShake] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const inputValue = mainCoin === "usd" ? usdValue : clpValue;
  const setInputValue = mainCoin === "usd" ? setUsdValue : setClpValue;

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    inputRef.current?.focus();
  }, [mainCoin]);

  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter', '.', ','];
    const isNumber = /^[0-9]$/.test(e.key);
    const isAllowed = allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey;
    
    if (!isNumber && !isAllowed) {
      e.preventDefault();
      setErrorMessage("Solo números");
      setShake(true);
      setTimeout(() => {
        setErrorMessage("");
        setShake(false);
      }, 1500);
    }
  };

  useEffect(() => {
    const input = inputRef.current;
    if (input) {
      input.addEventListener('wheel', handleWheel, { passive: false });
      return () => input.removeEventListener('wheel', handleWheel);
    }
  }, [handleWheel]);

  const formatNumber = (value: string): string => {
    if (!value) return "";
    const num = parseFloat(value.replace(/,/g, '.'));
    if (isNaN(num)) return value;
    return new Intl.NumberFormat('es-CL').format(num);
  };

  const parseFormattedNumber = (value: string): string => {
    return value.replace(/\./g, '').replace(/,/g, '.');
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let rawValue = event.target.value;
    rawValue = parseFormattedNumber(rawValue);
    
    if (rawValue === "" || /^[0-9]*\.?[0-9]*$/.test(rawValue)) {
      setInputValue(rawValue);
      setDollars(rawValue || "0");
      setErrorMessage("");
    }
  };

  const handleRadioChange = (value: string) => {
    setMainCoin(value);
    const newInputValue = value === "usd" ? usdValue : clpValue;
    setDollars(newInputValue || "0");
  };

  const handleResetValue = () => {
    setInputValue("");
    setDollars("0");
    inputRef.current?.focus();
  };

  const resultText = handleCoinChange(inputValue, +dollars);
  const resultValue = resultText.split(': ')[1] || '0';
  const resultLabel = mainCoin === "usd" ? "CLP" : "USD";
  const displayValue = formatNumber(inputValue);

  return (
    <div className="w-full max-w-lg mx-auto">
      {/* Toggle buttons */}
      <div className="flex rounded-xl bg-blue-950/50 p-1 mb-6">
        <button
          className={`flex-1 py-3 px-4 rounded-lg text-base font-semibold transition-all duration-300 relative ${
            mainCoin === "usd"
              ? "bg-blue-500 text-white shadow-lg shadow-blue-500/30"
              : "text-blue-300 hover:text-white"
          }`}
          onClick={() => handleRadioChange("usd")}
        >
          USD → CLP
          {usdValue && mainCoin !== "usd" && (
            <span className="absolute top-2 right-2 w-2 h-2 bg-blue-400 rounded-full"></span>
          )}
        </button>
        <button
          className={`flex-1 py-3 px-4 rounded-lg text-base font-semibold transition-all duration-300 relative ${
            mainCoin === "clp"
              ? "bg-blue-500 text-white shadow-lg shadow-blue-500/30"
              : "text-blue-300 hover:text-white"
          }`}
          onClick={() => handleRadioChange("clp")}
        >
          CLP → USD
          {clpValue && mainCoin !== "clp" && (
            <span className="absolute top-2 right-2 w-2 h-2 bg-blue-400 rounded-full"></span>
          )}
        </button>
      </div>

      {/* Input section */}
      <div className="mb-6 relative">
        <input
          ref={inputRef}
          id="title-input"
          type="text"
          inputMode="decimal"
          value={displayValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="0"
          autoComplete="off"
          className={`w-full px-4 py-5 text-5xl font-bold text-white bg-blue-950/50 border-2 rounded-2xl focus:outline-none focus:bg-blue-950/70 transition-all text-center placeholder:text-blue-700 ${
            shake ? "border-red-500 animate-shake" : "border-blue-800/50 focus:border-blue-500"
          }`}
        />
        {inputValue && (
          <button
            onClick={handleResetValue}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-blue-400 hover:text-white hover:bg-blue-700/50 rounded-full transition-all"
            aria-label="Limpiar"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        )}
        {errorMessage && (
          <p className="text-red-400 text-xs text-center mt-2 animate-fade-in">{errorMessage}</p>
        )}
      </div>

      {/* Result display - always rendered with transition */}
      <div 
        className={`bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-center shadow-2xl shadow-blue-500/20 transition-all duration-300 ease-out ${
          inputValue && parseFloat(inputValue) > 0
            ? "opacity-100 translate-y-0 scale-100"
            : "opacity-0 translate-y-4 scale-95 pointer-events-none"
        }`}
      >
        <p className="text-blue-100 text-xs uppercase tracking-widest mb-2">
          {mainCoin === "usd" ? "Pesos Chilenos" : "Dólares"}
        </p>
        <p className="text-white text-5xl font-bold tracking-tight">
          {resultValue}
        </p>
        <p className="text-blue-200 text-sm mt-2 font-medium">{resultLabel}</p>
      </div>
    </div>
  );
};

export default Form;
